import type { Restrictable } from '@empathyco/playboard-core-domain'
import type { ComputedRef } from 'vue'
import { computed, getCurrentInstance } from 'vue'
import { RestrictedValueEnum } from '../enums/restricted-value.enum'
import { AUTH_CONSTS } from '../store/auth/auth.consts'
import { CONFIG_CONSTS } from '../store/config/config.consts'
import { mapGetters } from './utils'

interface ToolingRestrictedComposable {
  userRoles: ComputedRef<Array<string>>
  getRestrictedValue: (roles: Restrictable['restrictedRoles']) => RestrictedValueEnum
  isRestricted: (roles: Restrictable['restrictedRoles']) => boolean
}

export function useToolingRestricted(): ToolingRestrictedComposable {
  const ctx = getCurrentInstance()

  if (!ctx) {
    throw new Error("'ctx' is not defined")
  }

  const { authorization } = mapGetters(ctx.proxy.$store, AUTH_CONSTS.moduleName, {
    authorization: AUTH_CONSTS.getters.tokenInfo,
  })

  const { currentInstance } = mapGetters(ctx.proxy.$store, CONFIG_CONSTS.moduleName, {
    currentInstance: CONFIG_CONSTS.getters.currentInstance,
  })

  const userRoles = computed(() => {
    const { '*': globalRoles, [currentInstance.value.id]: instanceRoles } =
      authorization.value.authorization.roles

    return [...new Set([...(globalRoles || []), ...(instanceRoles || [])])]
  })

  function getRestrictedValue(roles: Restrictable['restrictedRoles']): RestrictedValueEnum {
    if (!roles.length) {
      return RestrictedValueEnum.NONE
    }

    if (userRoles.value.some((role: string) => roles.includes(role))) {
      return RestrictedValueEnum.WRITE
    }

    return RestrictedValueEnum.READ
  }

  function isRestricted(roles: Restrictable['restrictedRoles']): boolean {
    return getRestrictedValue(roles) === RestrictedValueEnum.READ
  }

  return {
    userRoles,
    getRestrictedValue,
    isRestricted,
  }
}
